/**
 * Privacy statement.
 *
 * Written against what the code actually does rather than what a template says
 * a privacy policy usually contains: the cookie is the one the server sets, the
 * retention window is the one in `litestream.yml`, and the list of third
 * parties is short because there are none.
 */

import { Link } from 'react-router-dom';
import { DocSection, PublicPage } from './PublicPage.js';

export function Privacy(): JSX.Element {
  return (
    <PublicPage
      title="Privacy"
      updated="29 August 2026"
      lede={<>
        What FlightSlots holds, why it holds it, and how to get it back or have it deleted. This
        describes the software as it is built; a school that runs its own copy is the custodian of
        the records inside it.
      </>}
    >
      <DocSection title="What we hold">
        <p>A flight school's schedule is mostly information about people, so this list is specific.</p>
        <ul>
          <li><strong>Account.</strong> Name, email address, role, and a password stored only as a scrypt hash with a per-account salt. Plain passwords are never written down and cannot be recovered from what we keep.</li>
          <li><strong>Training records.</strong> Lessons, grades, endorsements, stage checks, solo authorisations, and the instructor who signed each one.</li>
          <li><strong>Certificates and currency.</strong> Medical class and expiry, written test results, CFI and instructor certificate dates, and the TSA citizenship check where a school records one.</li>
          <li><strong>Operations.</strong> Reservations, Hobbs and tach entries, squawks, inspections and aircraft status.</li>
          <li><strong>Money.</strong> Invoices, line items and account balances. Card numbers are not held: FlightSlots does not take payments.</li>
          <li><strong>Messages.</strong> Notifications and any email the system generated, kept as the school's record of what was sent.</li>
          <li><strong>An audit log</strong> of who changed what, which is the point of having one.</li>
        </ul>
      </DocSection>

      <DocSection title="Why we hold it">
        <p>
          To run the schedule, and to let a school show its work. A training record that cannot be
          produced later is not a training record, and under Part 141 it is the thing an audit asks
          for. Nothing here is collected to build a profile of you, and none of it is used to decide
          anything about you beyond what your school is already deciding.
        </p>
      </DocSection>

      <DocSection title="Cookies">
        <p>
          One cookie, <code>fs_session</code>. It holds a random session token and nothing else — no
          name, no email, no identifier that means anything outside our database. It is
          <code>HttpOnly</code>, so scripts on the page cannot read it, <code>SameSite=Lax</code>,
          and marked <code>Secure</code> over HTTPS. It lasts thirty days, and signing out ends the
          session on the server rather than only in your browser.
        </p>
        <p>
          There are no analytics cookies, advertising cookies or tracking pixels, so there is no
          consent banner to dismiss. Your appearance preference is kept in your browser's local
          storage and never sent to us.
        </p>
      </DocSection>

      <DocSection title="Who else sees it">
        <p>
          No advertising networks, no analytics providers, no data brokers, and no training of
          machine-learning models on your records. FlightSlots relies on two kinds of supplier and
          you can name both:
        </p>
        <ul>
          <li><strong>Hosting.</strong> The application and its database run on infrastructure we rent. The provider can technically reach the disk, as any host can.</li>
          <li><strong>Backup storage.</strong> Where a replica is configured, the database is streamed continuously to object storage so a failed machine costs seconds rather than a day.</li>
        </ul>
        <p>
          Beyond that we disclose records only when the school asks us to, or where the law requires
          it and we are permitted to say so.
        </p>
      </DocSection>

      <DocSection title="How long we keep it">
        <p>
          Live records last as long as the school keeps them. Deleting a record in the application
          removes it from the live database immediately.
        </p>
        <p>
          Backups are the exception worth stating plainly: the continuous replica keeps thirty days
          of restore points, so a deleted record can persist in a backup for up to thirty days after
          it is gone from the application, and then ages out. We do not restore backups to recover
          data a school deliberately deleted.
        </p>
      </DocSection>

      <DocSection title="What you can ask for">
        <p>You can ask us, at any time, to:</p>
        <ul>
          <li>send you a copy of what we hold about you, in a form you can actually read;</li>
          <li>correct something that is wrong;</li>
          <li>delete your account and the records tied to it, subject to what your school is required to retain;</li>
          <li>explain anything on this page in more detail.</li>
        </ul>
        <p>
          Use the <Link to="/contact?about=privacy">privacy and records form</Link>. We answer within
          one business day and complete the request within thirty days.
        </p>
        <p>
          One limit worth being honest about: if you are a student or an instructor at a school, your
          training records belong to that school's account. We will always tell you what we hold and
          give you a copy, but deleting a record the school is required to keep is the school's
          decision, not ours.
        </p>
      </DocSection>

      <DocSection title="Children">
        <p>
          Student pilots can be young, and a school may hold records for a minor. FlightSlots is sold
          to and used by the school rather than the student, and accounts are created by the school
          or joined with a code the school issues. We do not knowingly collect information directly
          from a child outside that relationship.
        </p>
      </DocSection>

      <DocSection title="Security">
        <p>
          How the data is protected in transit and at rest, and what happens if something goes wrong,
          is on the <Link to="/security">security page</Link>.
        </p>
      </DocSection>

      <DocSection title="Changes">
        <p>
          If this statement changes in a way that affects what we collect or who sees it, we will say
          so in the application rather than quietly editing this page and changing the date.
        </p>
      </DocSection>
    </PublicPage>
  );
}
